import { z } from "zod";

import { ListDataSourceSchema } from "./list-data-source.js";

// ── Profile I/O fields: single source of truth for import & export ──

/**
 * Fields available for column mapping during import (CSV/Excel/JSON).
 * These are the flat fields that map directly to DB profile columns.
 */
export const PROFILE_IMPORT_FIELDS = [
    "linkedin_url",
    "linkedin_id",
    "full_name",
    "first_name",
    "last_name",
    "headline",
    "about",
    "profile_image_url",
    "job_title",
    "company",
    "company_logo_url",
    "company_industry",
    "company_employee_range",
    "city",
    "country",
    "email",
    "phone",
    "exit_company_name",
    "exit_company_linkedin_url",
    "exit_date",
    "exit_amount",
    "exit_currency",
    "connection_count",
    "follower_count",
    "school",
    "types",
] as const;

export type ProfileImportField = (typeof PROFILE_IMPORT_FIELDS)[number];

/**
 * The exit company (#709): mapped by an administrator only. A client's file
 * keeps its date, amount and currency columns.
 */
export const ADMIN_ONLY_IMPORT_FIELDS = ["exit_company_name", "exit_company_linkedin_url"] as const satisfies readonly ProfileImportField[];

/**
 * The columns that make a file a file of exits (#709): once one of them is
 * mapped, a row whose profile is not in the database is rejected rather than
 * created. The currency alone says nothing without an amount.
 */
export const EXIT_IMPORT_FIELDS = [
    "exit_company_name",
    "exit_company_linkedin_url",
    "exit_date",
    "exit_amount",
] as const satisfies readonly ProfileImportField[];

/** Whether a column mapping maps at least one exit column. */
export function mapsExitColumns(columnMapping: Record<string, string>): boolean {
    return Object.values(columnMapping).some((f) => (EXIT_IMPORT_FIELDS as readonly string[]).includes(f));
}

/** @deprecated Use PROFILE_IMPORT_FIELDS instead */
export const CSV_PROFILE_FIELDS = PROFILE_IMPORT_FIELDS;
/** @deprecated Use ProfileImportField instead */
export type CsvProfileField = ProfileImportField;

/**
 * Fields included in profile exports (CSV/Excel/JSON download).
 * Superset that also includes computed/read-only fields not mappable during import.
 */
export const PROFILE_EXPORT_FIELDS = [
    "full_name", "first_name", "last_name", "types",
    "job_title", "company", "company_industry", "company_employee_range",
    "city", "country", "email", "email_status", "phone", "phone_status", "linkedin_url",
    "connection_count", "follower_count",
    "is_ceo", "is_founder", "is_ex_ceo", "is_ex_founder",
    "has_real_estate_knowledge", "has_financial_knowledge", "is_multiple_investor",
    "total_years_worked", "total_distinct_roles", "total_distinct_companies",
    "experiences", "educations",
] as const;

export type ProfileExportField = (typeof PROFILE_EXPORT_FIELDS)[number];

/** Human-readable labels for each export field. */
export const PROFILE_EXPORT_FIELD_LABELS: Record<ProfileExportField, string> = {
    full_name: "Full Name",
    first_name: "First Name",
    last_name: "Last Name",
    job_title: "Job Title",
    company: "Company",
    company_industry: "Industry",
    company_employee_range: "Company Size",
    city: "City",
    country: "Country",
    email: "Email",
    email_status: "Email status",
    phone: "Phone",
    phone_status: "Phone status",
    linkedin_url: "LinkedIn URL",
    connection_count: "Connections",
    follower_count: "Followers",
    is_ceo: "CEO",
    is_founder: "Founder",
    is_ex_ceo: "Ex-CEO",
    is_ex_founder: "Ex-Founder",
    has_real_estate_knowledge: "Real Estate Knowledge",
    has_financial_knowledge: "Financial Knowledge",
    is_multiple_investor: "Multiple Investor",
    types: "Types",
    total_years_worked: "Total Years Worked",
    total_distinct_roles: "Total Distinct Roles",
    total_distinct_companies: "Total Distinct Companies",
    experiences: "Experiences (JSON)",
    educations: "Educations (JSON)",
};

/** Logical groupings of export fields for the column picker UI. */
export const PROFILE_EXPORT_FIELD_GROUPS: { label: string; fields: ProfileExportField[] }[] = [
    {
        label: "Identity",
        fields: ["full_name", "first_name", "last_name", "types"],
    },
    {
        label: "Professional",
        fields: ["job_title", "company", "company_industry", "company_employee_range"],
    },
    {
        label: "Location",
        fields: ["city", "country"],
    },
    {
        label: "Contact",
        fields: ["email", "phone", "linkedin_url"],
    },
    {
        label: "Network",
        fields: ["connection_count", "follower_count"],
    },
    {
        label: "Flags",
        fields: [
            "is_ceo", "is_founder", "is_ex_ceo", "is_ex_founder",
            "has_real_estate_knowledge", "has_financial_knowledge",
            "is_multiple_investor",
        ],
    },
    {
        label: "Career Summary",
        fields: ["total_years_worked", "total_distinct_roles", "total_distinct_companies"],
    },
    {
        label: "Experiences",
        fields: ["experiences"],
    },
    {
        label: "Education",
        fields: ["educations"],
    },
];

// ── Company export fields (extracted from profile experiences) ──

export const COMPANY_EXPORT_FIELDS = [
    "name", "industry", "employee_range", "employee_count",
    "hq_city", "hq_country", "hq_continent", "hq_region",
    "domain", "website", "linkedin_url",
    "year_founded", "description",
    "specialties", "company_type",
    "follower_count",
    "related_profiles",
] as const;

export type CompanyExportField = (typeof COMPANY_EXPORT_FIELDS)[number];

/** Human-readable labels for each company export field. */
export const COMPANY_EXPORT_FIELD_LABELS: Record<CompanyExportField, string> = {
    name: "Company Name",
    industry: "Industry",
    employee_range: "Size Range",
    employee_count: "Employee Count",
    hq_city: "HQ City",
    hq_country: "HQ Country",
    hq_continent: "HQ Continent",
    hq_region: "HQ Region",
    domain: "Domain",
    website: "Website",
    linkedin_url: "LinkedIn URL",
    year_founded: "Year Founded",
    description: "Description",
    specialties: "Specialties",
    company_type: "Company Type",
    follower_count: "Followers",
    related_profiles: "Related Profiles (JSON)",
};

/** Logical groupings of company export fields for the column picker UI. */
export const COMPANY_EXPORT_FIELD_GROUPS: { label: string; fields: CompanyExportField[] }[] = [
    {
        label: "Identity",
        fields: ["name", "industry", "company_type"],
    },
    {
        label: "Size",
        fields: ["employee_range", "employee_count", "follower_count"],
    },
    {
        label: "Location",
        fields: ["hq_city", "hq_country", "hq_continent", "hq_region"],
    },
    {
        label: "Online",
        fields: ["domain", "website", "linkedin_url"],
    },
    {
        label: "Details",
        fields: ["year_founded", "description", "specialties"],
    },
    {
        label: "Traceability",
        fields: ["related_profiles"],
    },
];

/** Data types available in the export flow. */
export type ExportDataType = "profiles" | "companies";

// ── POST /csv/parse: response ──

export const CsvParseResponseSchema = z.object({
    headers: z.array(z.string()),
    row_count: z.number().int(),
    rows: z.array(z.record(z.string(), z.string())),
    preview: z.array(z.record(z.string(), z.string())),
    auto_mapping: z.record(z.string(), z.string()),
    errors: z.array(z.string()),
});
export type CsvParseResponse = z.infer<typeof CsvParseResponseSchema>;

// ── POST /csv/analyze: request ──

export const CsvAnalyzeRequestSchema = z.object({
    column_mapping: z.record(z.string(), z.string()),
    rows: z.array(z.record(z.string(), z.string())),
});
export type CsvAnalyzeRequest = z.infer<typeof CsvAnalyzeRequestSchema>;

// ── Profile preview (for analyze response) ──

export const ProfilePreviewSchema = z.object({
    linkedin_url: z.string(),
    full_name: z.string().nullable(),
    job_title: z.string().nullable(),
    company: z.string().nullable(),
    city: z.string().nullable(),
    country: z.string().nullable(),
});
export type ProfilePreview = z.infer<typeof ProfilePreviewSchema>;

export const ProfileUpdatePreviewSchema = z.object({
    linkedin_url: z.string(),
    full_name: z.string().nullable(),
    status: z.enum(["stale", "fresh"]),
    fields_to_update: z.array(z.string()),
});
export type ProfileUpdatePreview = z.infer<typeof ProfileUpdatePreviewSchema>;

// ── Exit conflicts (#709) ──

/** An exit's values, as the analyze step shows them side by side. */
export const ExitValuesSchema = z.object({
    company_name: z.string().nullable(),
    company_linkedin_url: z.string().nullable(),
    /** ISO date; read with its precision (a "2021" is stored on 1 January). */
    exit_date: z.string().nullable(),
    exit_date_precision: z.enum(["year", "month", "day"]).nullable(),
    exit_amount: z.number().nullable(),
    exit_currency: z.string().nullable(),
});
export type ExitValues = z.infer<typeof ExitValuesSchema>;

/**
 * A row whose exit would change one already on file (#709); the administrator
 * decides, row by row, whether to overwrite it.
 */
export const ExitConflictSchema = z.object({
    row: z.number().int(),
    linkedin_url: z.string(),
    full_name: z.string().nullable(),
    reason: z.enum(["company_values_differ", "same_year_without_company", "same_year_values_differ"]),
    stored: ExitValuesSchema,
    incoming: ExitValuesSchema,
});
export type ExitConflict = z.infer<typeof ExitConflictSchema>;

// ── POST /csv/analyze: response ──

export const CsvAnalyzeResponseSchema = z.object({
    total: z.number().int(),
    to_create: z.number().int(),
    to_update: z.number().int(),
    to_skip: z.number().int(),
    duplicates_in_file: z.number().int(),
    existing_profile_ids: z.array(z.string()).default([]),
    preview_create: z.array(ProfilePreviewSchema),
    preview_update: z.array(ProfileUpdatePreviewSchema),
    /** Rows that will not import (no valid LinkedIn URL). */
    errors: z.array(z.object({
        row: z.number().int(),
        error: z.string(),
    })),
    /** Cells left unchanged on a row that still imports (an unreadable exit date or amount, #654). */
    warnings: z.array(z.object({
        row: z.number().int(),
        error: z.string(),
    })),
    /** The file maps an exit column (#709): an unknown profile carrying an exit is rejected, not created. */
    maps_exits: z.boolean(),
    /** Rows rejected because they carry an exit for a profile not in the database (#709). */
    profiles_not_found: z.number().int(),
    exits_to_create: z.number().int(),
    exits_to_update: z.number().int(),
    /** Every row that would change an exit on file, for the administrator to decide. */
    exit_conflicts: z.array(ExitConflictSchema),
});
export type CsvAnalyzeResponse = z.infer<typeof CsvAnalyzeResponseSchema>;

// ── POST /csv/import: request ──

export const CsvImportRequestSchema = z.object({
    list_id: z.uuid().optional(),
    import_name: z.string().min(1).optional(),
    source: z.enum(["csv_import", "excel_import", "json_import"]).optional(),
    /**
     * Client flow (#748): the uploaded file's name, shown to Invyo in the alert
     * about the new import. Not stored.
     */
    file_name: z.string().max(255).optional(),
    /**
     * Where the file's data came from (#749). Required when the import creates
     * its list (no `list_id`); ignored with a `list_id`.
     */
    data_source: ListDataSourceSchema.optional(),
    column_mapping: z.record(z.string(), z.string()),
    rows: z.array(z.record(z.string(), z.string())),
    /**
     * Rows whose exit conflict the administrator chose to overwrite (#709); any
     * other conflicting row is skipped. Honoured for an administrator only.
     */
    overwrite_rows: z.array(z.number().int()).optional(),
});
/** The body a caller sends (input shape: `data_source` before its normalization). */
export type CsvImportRequest = z.input<typeof CsvImportRequestSchema>;

// ── POST /csv/import: response ──

export const CsvImportResponseSchema = z.object({
    total: z.number().int(),
    created: z.number().int(),
    updated: z.number().int(),
    skipped: z.number().int(),
    companies_created: z.number().int(),
    schools_created: z.number().int(),
    list_id: z.uuid().optional(),
    /** The profiles this import created: the wizard enriches them afterwards (PI-115). */
    created_profile_ids: z.array(z.uuid()),
    /** The file's rows (#709): imported, and left out (profile not found, conflict skipped, invalid URL). */
    rows_total: z.number().int(),
    rows_imported: z.number().int(),
    rows_not_processed: z.number().int(),
    exits_created: z.number().int(),
    exits_updated: z.number().int(),
    /** Every row left out (profile not found, conflict skipped, invalid URL), with its line. */
    errors: z.array(z.object({
        row: z.number().int(),
        linkedin_url: z.string(),
        error: z.string(),
    })),
    /** Cells left unchanged on a row that imported (an unreadable exit cell), with its line. */
    warnings: z.array(z.object({
        row: z.number().int(),
        error: z.string(),
    })),
});
export type CsvImportResponse = z.infer<typeof CsvImportResponseSchema>;
